import { Info, Lock, Snowflake } from "lucide-react";

import { cn } from "@/lib/utils";

import type { LockStatus } from "../api";

/**
 * Renders a per-page banner reflecting the ``lock_status`` field on each
 * tab's payload. Visible only when the status is NOT ``"live"`` — for
 * live current-week views the banner stays out of the way.
 *
 * Three states:
 *
 *  - ``frozen``: Monday cron has snapshotted the week so live queries
 *    no longer overwrite the displayed numbers, but Stan can still edit
 *    notes / call values / AE submissions. A subtle blue chip.
 *
 *  - ``locked``: Stan has explicitly locked the week (after the meeting,
 *    via the FilterBar lock button + confirm modal). All writes to that
 *    week return 423. A green chip with a lock icon.
 *
 *  - ``past_unlocked``: the week is in the past but no snapshot exists.
 *    The page renders an empty payload (zeros / empty arrays) and this
 *    banner sits at the top explaining why. Yellow / attention-grabbing.
 */
export function LockedBanner({
  status,
  isoYear,
  isoWeek,
}: {
  status: LockStatus | undefined;
  isoYear: number;
  isoWeek: number;
}) {
  if (!status || status === "live") return null;

  const label = `Wk ${isoWeek} ${isoYear}`;

  if (status === "past_unlocked") {
    return (
      <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
        <div className="flex-1">
          <p className="font-semibold text-warning">
            {label} wasn't snapshotted into cinch_ops.
          </p>
          <p className="text-xs text-muted-foreground">
            Past weeks only display data that was captured at the time —
            the tool no longer re-queries Snowflake / HubSpot / Cinch for
            historical weeks. Use the Lock button on a captured week to
            preserve it, or backfill from Stan's spreadsheet.
          </p>
        </div>
      </div>
    );
  }

  if (status === "locked") {
    return (
      <div className="flex items-center gap-2 rounded-md border border-success/40 bg-success/10 px-3 py-1.5 text-xs">
        <Lock className="h-3.5 w-3.5 shrink-0 text-success" />
        <span className="font-semibold text-success">
          {label} is locked.
        </span>
        <span className="text-muted-foreground">
          Showing the snapshot Stan locked in — edits are blocked.
        </span>
      </div>
    );
  }

  // frozen
  return (
    <div className={cn(
      "flex items-center gap-2 rounded-md border border-power/30 bg-power/5 px-3 py-1.5 text-xs",
    )}>
      <Snowflake className="h-3.5 w-3.5 shrink-0 text-power" />
      <span className="font-semibold text-power">
        {label} frozen (Monday snapshot).
      </span>
      <span className="text-muted-foreground">
        Queried numbers are pinned; notes &amp; calls still editable until Stan locks.
      </span>
    </div>
  );
}
